import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import PushNotificationToggle from '../components/PushNotificationToggle';
import { useAuth } from '../context/AuthContext';
import { Notification, UserRole } from '../types';

function formatDateTime(value?: string | Date) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const NotificationsPage = () => {
  const { user } = useAuth();
  const isApplicant = user?.role === UserRole.APPLICANT;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [markingReadId, setMarkingReadId] = useState<number | null>(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  // Accurate unread total from the server, not derived from the capped
  // list below.
  const [unreadCount, setUnreadCount] = useState(0);

  const load = async () => {
    try {
      // The list is capped server-side (newest 50), so the unread total
      // cannot be derived from it — with more than 50 notifications that
      // undercounts, and disagrees with the sidebar badge, which uses
      // this same count endpoint. Fetch both so the header is accurate.
      const [result, unread] = await Promise.all([
        apiService.getNotifications(),
        apiService.getUnreadNotificationCount()
      ]);
      setNotifications(result);
      setUnreadCount(unread);
    } catch (err: any) {
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleMarkRead = async (id: number) => {
    setMarkingReadId(id);
    setError('');
    try {
      await apiService.markNotificationAsRead(id);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to update notification');
    } finally {
      setMarkingReadId(null);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    setError('');
    try {
      await apiService.markAllNotificationsAsRead();
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to update notifications');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setError('');
    try {
      await apiService.deleteNotification(id);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete notification');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    setError('');
    try {
      await apiService.deleteAllNotifications();
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete notifications');
    } finally {
      setDeletingAll(false);
    }
  };


  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">Notifications</div>
        {notifications.length > 0 && (
          <div className="navbar-actions" style={{ display: 'flex', gap: '0.5rem' }}>
            {unreadCount > 0 && (
              <button className="btn btn-outline btn-sm" disabled={markingAllRead} onClick={handleMarkAllRead}>
                {markingAllRead ? 'Marking...' : 'Mark all as read'}
              </button>
            )}
            <button
              className="btn btn-outline btn-sm"
              style={{ color: 'var(--error-text)', borderColor: 'var(--error-text)' }}
              disabled={deletingAll}
              onClick={handleDeleteAll}
            >
              {deletingAll ? 'Deleting...' : 'Delete All'}
            </button>
          </div>
        )}
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>Notifications</h1>
          <p>{unreadCount > 0 ? `${unreadCount} unread` : 'You are all caught up.'}</p>
        </div>

        <PushNotificationToggle />

        {isApplicant && (
          <div
            style={{
              padding: '0.85rem 1rem',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1.5rem',
              color: '#FBBF24',
              fontSize: '0.85rem'
            }}
          >
            Notifications are automatically removed 2 days after they arrive — save anything important elsewhere.
          </div>
        )}

        {error && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : notifications.length === 0 ? (
          <div className="card">
            <p style={{ color: 'var(--text-secondary)' }}>No notifications yet.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="card"
                style={{
                  borderLeft: notification.isRead ? undefined : '3px solid #8B5CF6',
                  opacity: notification.isRead ? 0.7 : 1
                }}
              >
                {/* Wraps, and the text column can shrink — otherwise the
                    non-shrinking button pair and a text block that also
                    refuses to go below its content width push the row
                    past the viewport, scrolling the whole page sideways. */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ flex: '1 1 16rem', minWidth: 0 }}>
                    <strong>{notification.title}</strong>
                    <p style={{ margin: '0.35rem 0', color: 'var(--text-secondary)' }}>{notification.message}</p>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{formatDateTime(notification.createdAt)}</span>
                    {notification.actionUrl && (
                      <>
                        {' · '}
                        <Link to={notification.actionUrl} style={{ fontSize: '0.8rem' }}>
                          View
                        </Link>
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    {!notification.isRead && (
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={markingReadId === notification.id}
                        onClick={() => handleMarkRead(notification.id)}
                      >
                        {markingReadId === notification.id ? 'Marking...' : 'Mark as read'}
                      </button>
                    )}
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ color: 'var(--error-text)', borderColor: 'var(--error-text)' }}
                      disabled={deletingId === notification.id}
                      onClick={() => handleDelete(notification.id)}
                    >
                      {deletingId === notification.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
